// Theme color utilities shared across ci-pipeline canvas hooks.
// Reads `--c-*` CSS variables from :root.
//
// `cssVar` reads through on every call. `colors()` memoises, and the
// cache is invalidated by a single module-level MutationObserver on
// `<html data-theme>` — `ThemeApply` (hooks/theme-apply.ts) flips that
// attribute at runtime, so every canvas hook holding a `colors()`
// result now re-reads on the next call after a theme switch instead
// of painting the previous theme until it remounts.

// Raw read, no fallback. Anything that is not a `--c-*` colour triple — a
// font stack, a length — has to see the empty string and choose its own
// default rather than inherit the black-triple one `cssVar` layers on.
export const cssVarRaw = (name: string): string =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim();

export const cssVar = (name: string): string => cssVarRaw(name) || "0 0 0";

export const cssRgb = (v: string, a = 1): string => `rgb(${v} / ${a})`;

export interface ThemeColors {
  danger: string;
  info: string;
  warning: string;
  accent: string;
  text: string;
  textMuted: string;
  textSub: string;
  bg: string;
  bgDark: string;
  bgActive: string;
  border: string;
}

let _colors: ThemeColors | null = null;
let _observerInstalled = false;

const readColors = (): ThemeColors => ({
  danger: cssVar("--c-danger"),
  info: cssVar("--c-info"),
  warning: cssVar("--c-warning"),
  accent: cssVar("--c-accent"),
  text: cssVar("--c-text"),
  textMuted: cssVar("--c-text-tertiary"),
  textSub: cssVar("--c-text-secondary"),
  bg: cssVar("--c-bg"),
  bgDark: cssVar("--c-bg-secondary"),
  bgActive: cssVar("--c-bg-active"),
  border: cssVar("--c-border"),
});

const installThemeObserver = (): void => {
  if (_observerInstalled || typeof MutationObserver === "undefined") return;
  _observerInstalled = true;
  new MutationObserver((records) => {
    if (records.some((r) => r.attributeName === "data-theme")) {
      _colors = null;
    }
  }).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-theme"],
  });
};

export const colors = (): ThemeColors => {
  installThemeObserver();
  if (_colors === null) {
    _colors = readColors();
  }
  return _colors;
};
